import { useRef, useState, useCallback, useContext, createContext } from 'react';
import { jsx } from 'react/jsx-runtime';

/**
 * Borrows from @react-aria/focus
 * https://github.com/reactjs/rfcs/issues/104
 */
const FocusContext = /*#__PURE__*/createContext();
const focusableElements = ['input:not([type=hidden])', 'select', 'textarea', 'button', 'a[href]', 'area[href]', 'summary', 'iframe', 'object', 'embed', 'audio[controls]', 'video[controls]', '[contenteditable]', '[tabindex]'];
const FOCUSABLE_ELEMENT_SELECTOR = focusableElements.join(',');
const getFocusableElements = (container, options) => {
  if (!container) {
    return [];
  }
  const focusableElements = [...container.querySelectorAll(FOCUSABLE_ELEMENT_SELECTOR)].filter(el => el.hasAttribute('disabled') || options?.tabbable && !(el.tabIndex > -1) ? false : true);
  return focusableElements;
};
const createFocusManager = elements => {
  return {
    focusFirst() {
      const focusable = elements;
      if (!focusable) {
        return;
      }
      const nextNode = focusable[0];
      if (nextNode) {
        nextNode.focus();
      }
      return nextNode;
    },
    focusLast() {
      const focusable = elements;
      if (!focusable) {
        return;
      }
      const nextNode = focusable[focusable.length - 1];
      if (nextNode) {
        nextNode.focus();
      }
      return nextNode;
    },
    focusNext() {
      const focusable = elements;
      if (!focusable) {
        return;
      }
      const activeElement = document.activeElement;
      const currentIndex = focusable.indexOf(activeElement);

      // If there is no focused element or the current focused element is last, start over
      const lastIndex = focusable.length - 1;
      const nextIndex = currentIndex === undefined || currentIndex === lastIndex ? 0 : currentIndex + 1;
      const nextNode = focusable[nextIndex];
      if (nextNode) {
        nextNode.focus();
      }
      return nextNode;
    },
    focusPrevious() {
      const focusable = elements;
      if (!focusable) {
        return;
      }
      const activeElement = document.activeElement;
      const currentIndex = focusable.indexOf(activeElement);

      // If there is no focused element or the current focused element is first, move to the end
      const lastIndex = focusable.length - 1;
      const previousIndex = currentIndex === undefined || currentIndex === 0 ? lastIndex : currentIndex - 1;
      const previousNode = focusable[previousIndex];
      if (previousNode) {
        previousNode.focus();
      }
      return previousNode;
    }
  };
};
const FocusableProvider = _ref => {
  let {
    children,
    options = {
      tabbable: true
    }
  } = _ref;
  const containerRef = useRef(null);
  const [elements, setElements] = useState(null);

  /** We want to be notified when the ref changes */
  const setRef = useCallback(node => {
    if (containerRef.current !== undefined) {
      containerRef.current = undefined;
    }
    if (node) {
      const focusableElements = getFocusableElements(node);
      setElements(focusableElements);
    }
    containerRef.current = node;
  }, []);

  /** Function to force recalculation of focusable elements */
  const updateElements = useCallback(() => {
    const focusableElements = getFocusableElements(containerRef?.current);
    setElements(focusableElements);
  }, []);

  /** Provide functions to focus next and prev  */
  const focusManager = createFocusManager(elements);
  const providerValue = {
    ...focusManager,
    focusableRef: setRef,
    updateElements
  };
  return /*#__PURE__*/jsx(FocusContext.Provider, {
    value: providerValue,
    children: children
  });
};
const useFocusable = () => {
  const context = useContext(FocusContext);
  if (context === undefined) {
    throw new Error('useFocusable must be used within a FocusableProvider');
  }
  return context;
};

export { FocusableProvider, useFocusable };
